// ============================================
// MODAL COMMON - Reusable Modal Store & Utilities
// ============================================
//
// This provides a global store for managing modals and utilities
// for body scroll locking.
//
// Usage with PHP component:
// 1. PHP component uses x-modal-common:register="modalId" to register
// 2. Open: $store.modalCommon.open('modalId') or showXxxModal = true
// 3. Close: $store.modalCommon.close('modalId') or showXxxModal = false
//
// The store automatically handles:
// - Locking the page's scroll when a modal opens, on <html> and <body> (utils/scroll-lock)
// - Restoring scroll when all modals close
// - Escape key closes only the topmost modal (stacked modals keep the one below open)
// - Stacking order: the last opened modal renders above the others (zIndex)
// ============================================

import Alpine from 'alpinejs';
import { lockScroll, unlockScroll } from '../../utils/scroll-lock';

interface ModalContext {
    urgent?: boolean;
    urgentMessage?: string;
    [key: string]: unknown;
}

interface ModalState {
    isOpen: boolean;
    context: ModalContext | null;
    onOpen?: () => void;
    onClose?: () => void;
}

interface ModalCommonStore {
    modals: Record<string, ModalState>;
    context: ModalContext | null;
    stack: string[];
    register(id: string, options?: { onOpen?: () => void; onClose?: () => void }): void;
    unregister(id: string): void;
    open(id: string, context?: ModalContext): void;
    close(id: string): void;
    closeTop(): void;
    closeAll(): void;
    toggle(id: string): void;
    isOpen(id: string): boolean;
    hasOpenModals(): boolean;
    zIndex(id: string): number;
}

const BASE_Z_INDEX = 9999;

export function registerModalCommon(): void {
    // Global store for managing all modals
    Alpine.store('modalCommon', {
        modals: {} as Record<string, ModalState>,
        context: null as ModalContext | null,
        // Open modal ids, oldest first; the last one is the topmost.
        stack: [] as string[],

        register(id: string, options: { onOpen?: () => void; onClose?: () => void } = {}) {
            if (!this.modals[id]) {
                this.modals[id] = {
                    isOpen: false,
                    context: null,
                    onOpen: options.onOpen,
                    onClose: options.onClose
                };
            }
        },

        unregister(id: string) {
            if (this.modals[id]?.isOpen) {
                this.close(id);
            }
            delete this.modals[id];
        },

        open(id: string, context: ModalContext | null = null) {
            if (!this.modals[id]) {
                this.register(id);
            }

            this.modals[id].isOpen = true;
            this.modals[id].context = context;
            this.context = context;
            this.stack = this.stack.filter(openId => openId !== id).concat(id);
            lockScroll('modal');

            // Call onOpen callback if exists
            if (this.modals[id].onOpen) {
                this.modals[id].onOpen();
            }
        },

        close(id: string) {
            this.stack = this.stack.filter(openId => openId !== id);

            if (this.modals[id]) {
                this.modals[id].isOpen = false;
                this.modals[id].context = null;

                // The modal left underneath keeps reading its own context.
                const top = this.stack[this.stack.length - 1];
                this.context = top ? (this.modals[top]?.context ?? null) : null;

                // Call onClose callback if exists
                if (this.modals[id].onClose) {
                    this.modals[id].onClose();
                }
            }

            // Only restore scroll if no other modals are open
            if (!this.hasOpenModals()) {
                unlockScroll('modal');
            }
        },

        closeTop() {
            const top = this.stack[this.stack.length - 1];
            if (top) {
                this.close(top);
            }
        },

        closeAll() {
            Object.keys(this.modals).forEach(id => {
                if (this.modals[id].isOpen) {
                    this.modals[id].isOpen = false;
                    if (this.modals[id].onClose) {
                        this.modals[id].onClose();
                    }
                }
            });
            this.stack = [];
            this.context = null;
            unlockScroll('modal');
        },

        toggle(id: string) {
            if (this.isOpen(id)) {
                this.close(id);
            } else {
                this.open(id);
            }
        },

        isOpen(id: string): boolean {
            return this.modals[id]?.isOpen ?? false;
        },

        hasOpenModals(): boolean {
            return Object.values(this.modals).some(modal => modal.isOpen);
        },

        // A modal opened on top of another one renders above it, whatever their order in the DOM.
        zIndex(id: string): number {
            return BASE_Z_INDEX + Math.max(0, this.stack.indexOf(id));
        }
    } as ModalCommonStore);

    // Directive: x-modal-common:register="modalId"
    // Registers a modal and syncs with local Alpine state
    Alpine.directive('modal-common', (el, { value, expression }, { cleanup }) => {
        const modalId = expression;

        if (value === 'register') {
            // Register modal on init
            const store = Alpine.store('modalCommon') as ModalCommonStore;
            store.register(modalId);

            // Cleanup on destroy
            cleanup(() => {
                store.unregister(modalId);
            });
        }
    });

    // Directive: x-modal-close
    // Closes the parent modal when clicked
    // Usage: <button x-modal-close>Close</button>
    Alpine.directive('modal-close', (el, _, { cleanup }) => {

        const handler = () => {
            // Find the parent modal container (the one with the modal id)
            // The structure is: div#modal-id > div.backdrop + div.modal-content > ... > button
            const modalContainer = el.closest('[id]');
            if (modalContainer && modalContainer.id) {
                (Alpine.store('modalCommon') as ModalCommonStore).close(modalContainer.id);
            } else {
                console.warn('[x-modal-close] No modal container found');
            }
        };

        el.addEventListener('click', handler);
        cleanup(() => el.removeEventListener('click', handler));
    });

    // Global escape key handler: closes only the topmost modal
    window.addEventListener('keydown', (e: KeyboardEvent) => {
        if (e.key === 'Escape') {
            const store = Alpine.store('modalCommon') as ModalCommonStore;
            store.closeTop();
        }
    });
}

// Export store type for external use
export type { ModalCommonStore };
